/*
 * Books' year end: the zip for the accountant, closing the books through a day (QuickBooks' closing date, Xero's lock
 * date), and journal entries (lines between categories that add up to 0, for what no bank shows: depreciation the
 * accountant gives, fees taken before the money came in, a loan's interest split from its principal).
 */
import { useState } from "react";
import type { BooksSetup } from "@steward/shared";
import { useAdjust, useAdjustments, useLock, useUndoAdjust } from "../../api/books";
import { downloadFile } from "../../api/client";
import { useSession } from "../../app/session";
import { InputText } from "../../components/fields";
import { moneyExact, shortDate } from "../../components/format";
import { FilterPick, Modal, useToast } from "../../components/overlays";
import { Banner, Button, Card, IconButton, StatusLabel } from "../../components/parts";
import { centsOf, errText } from "./common";

export function YearEnd({ setup, today }: { setup: BooksSetup; today: string }) {
  const { company } = useSession();
  const thisYear = Number(today.slice(0, 4));
  const [year, setYear] = useState(Number(today.slice(5, 7)) <= 4 ? thisYear - 1 : thisYear);
  const adjustments = useAdjustments(company.id, year);
  const undo = useUndoAdjust();
  const lock = useLock();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [journal, setJournal] = useState<"blank" | "fees" | null>(null);
  const [lockDay, setLockDay] = useState(setup.lockDay ?? `${String(thisYear - 1)}-12-31`);
  const cats = new Map(setup.categories.map((c) => [c.id, c.name]));
  const get = async () => {
    setBusy(true);
    const finish = toast(`Making the ${String(year)} year end files`, { busy: true });
    try {
      finish(
        (await downloadFile(`books/year-end?year=${String(year)}`, `books-${String(year)}.zip`))
          ? "Year end files downloaded"
          : "In the real app, the zip downloads here.",
      );
    } catch (e) {
      finish(errText(e), { kind: "error" });
    } finally {
      setBusy(false);
    }
  };
  const setLock = (day: string | null) =>
    void lock.mutateAsync(day).then(
      () => toast(day ? `Closed through ${shortDate(day)}` : "The books are open again"),
      (e: unknown) => toast(errText(e), { kind: "error" }),
    );
  return (
    <div className="stack">
      <div className="row tight">
        <FilterPick<string>
          label="Year"
          value={String(year)}
          onChange={(v) => setYear(Number(v))}
          options={[0, 1, 2, 3].map((n) => ({ value: String(thisYear - n), label: String(thisYear - n) }))}
        />
      </div>
      <div className="grid-2">
        <Card title={`For the accountant, ${String(year)}`}>
          <ul className="bk-checks">
            <li>Profit and loss by month, with each Schedule C line</li>
            <li>Every transaction with its category</li>
            <li>Contractors paid and the 1099s owed</li>
            <li>Each account's balance on Dec 31 and which months are reconciled</li>
            <li>Vehicles and equipment bought to keep</li>
          </ul>
          <Button icon="file" loading={busy} onClick={() => void get()}>
            Download the year end zip
          </Button>
        </Card>
        <Card title="Close the books">
          {setup.lockDay ? (
            <Banner type="success">Closed through {shortDate(setup.lockDay)}. Nothing on or before that day can change.</Banner>
          ) : (
            <p className="muted">
              After your taxes are filed, close the year so nothing in it changes by mistake. You can open it again.
            </p>
          )}
          <div className="row tight bk-lock">
            <InputText
              label="Closed through"
              type="date"
              value={lockDay}
              max={today}
              onChange={(e) => setLockDay(e.target.value)}
            />
            <Button
              icon="lock"
              loading={lock.isPending}
              disabled={!lockDay || lockDay === setup.lockDay}
              onClick={() => setLock(lockDay)}
            >
              Close
            </Button>
            {setup.lockDay ? (
              <Button type="tertiary" onClick={() => setLock(null)}>
                Open again
              </Button>
            ) : null}
          </div>
        </Card>
      </div>
      <Card
        title={`Journal entries, ${String(year)}`}
        action={
          <div className="row tight">
            <Button size="small" type="secondary" onClick={() => setJournal("fees")}>
              Jobber fees
            </Button>
            <Button size="small" icon="plus" onClick={() => setJournal("blank")}>
              New entry
            </Button>
          </div>
        }
      >
        <p className="muted">
          For what no bank shows, like depreciation from the accountant, or fees taken before the money came in. Each entry moves
          money between categories, so the bank does not change.
        </p>
        {(adjustments.data?.adjustments ?? []).length === 0 ? (
          <p className="muted small">None in {year}.</p>
        ) : (
          <ul className="bk-journal">
            {(adjustments.data?.adjustments ?? []).map((a) => (
              <li key={a.id} className={a.undone ? "undone" : undefined}>
                <div>
                  <b>{a.memo}</b> <span className="muted small">{shortDate(a.day)}</span>
                  <div className="muted small">
                    {a.lines
                      .map(
                        (l) =>
                          `${cats.get(l.categoryId) ?? ""} ${l.amountCents > 0 ? "plus" : "minus"} ${moneyExact(Math.abs(l.amountCents))}`,
                      )
                      .join(", ")}
                  </div>
                </div>
                {a.undone ? (
                  <StatusLabel status="inactive">Undone</StatusLabel>
                ) : (
                  <Button
                    type="tertiary"
                    size="small"
                    icon="undo"
                    onClick={() => void undo.mutateAsync(a.id).catch((e: unknown) => toast(errText(e), { kind: "error" }))}
                  >
                    Undo
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
      {journal ? <JournalModal setup={setup} year={year} preset={journal} onClose={() => setJournal(null)} /> : null}
    </div>
  );
}

interface JLine {
  categoryId: string;
  debit: string;
  credit: string;
}

/** A journal entry: debits and credits that must come out even, like QuickBooks' and Xero's. */
function JournalModal({
  setup,
  year,
  preset,
  onClose,
}: {
  setup: BooksSetup;
  year: number;
  preset: "blank" | "fees";
  onClose: () => void;
}) {
  const adjust = useAdjust();
  const toast = useToast();
  const cat = (code: string) => setup.categories.find((c) => c.code === code)?.id ?? "";
  const [day, setDay] = useState(`${String(year)}-12-31`);
  const [memo, setMemo] = useState(preset === "fees" ? `Jobber Payments fees for ${String(year)}` : "");
  const [lines, setLines] = useState<JLine[]>(
    preset === "fees"
      ? [
          { categoryId: cat("fees"), debit: "", credit: "" },
          { categoryId: cat("sales"), debit: "", credit: "" },
        ]
      : [
          { categoryId: "", debit: "", credit: "" },
          { categoryId: "", debit: "", credit: "" },
        ],
  );
  const [fees, setFees] = useState("");
  const shown = setup.categories.filter((c) => !c.hidden && c.type !== "open");
  const nameOf = new Map(shown.map((c) => [c.id, c.name]));
  const all =
    preset === "fees"
      ? (() => {
          const c = centsOf(fees) ?? 0;
          return [
            { categoryId: cat("fees"), amountCents: c },
            { categoryId: cat("sales"), amountCents: -c },
          ];
        })()
      : lines.map((l) => ({
          categoryId: l.categoryId,
          amountCents: (centsOf(l.debit || "0") ?? 0) - (centsOf(l.credit || "0") ?? 0),
        }));
  const used = all.filter((l) => l.amountCents !== 0);
  const debits = used.filter((l) => l.amountCents > 0).reduce((a, l) => a + l.amountCents, 0);
  const credits = -used.filter((l) => l.amountCents < 0).reduce((a, l) => a + l.amountCents, 0);
  const ok = memo.trim() && used.length >= 2 && used.every((l) => l.categoryId) && debits === credits && debits > 0;
  const go = async () => {
    try {
      await adjust.mutateAsync({ day, memo: memo.trim(), lines: used });
      toast("Journal entry added");
      onClose();
    } catch (e) {
      toast(errText(e), { kind: "error" });
    }
  };
  const set = (i: number, p: Partial<JLine>) => setLines(lines.map((l, j) => (j === i ? { ...l, ...p } : l)));
  return (
    <Modal
      title={preset === "fees" ? "Jobber Payments fees" : "New journal entry"}
      open
      onClose={onClose}
      size="large"
      footer={
        <Button onClick={() => void go()} loading={adjust.isPending} disabled={!ok}>
          Add
        </Button>
      }
    >
      <div className="stack">
        {preset === "fees" ? (
          <>
            <p className="muted">
              Jobber's card payments came in already short of Jobber's fees. Type the year's Jobber Payments fees from Jobber's
              report. They are added to Sales and to card fees, so the bank does not move.
            </p>
            <InputText
              label="Fees for the year"
              prefix="$"
              inputMode="decimal"
              value={fees}
              onChange={(e) => setFees(e.target.value)}
            />
          </>
        ) : null}
        <div className="form-grid">
          <InputText label="Day" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          <InputText label="What it is for" value={memo} maxLength={200} onChange={(e) => setMemo(e.target.value)} />
        </div>
        {preset === "blank" ? (
          <table className="bk-je">
            <thead>
              <tr>
                <th>Category</th>
                <th className="num">Debit</th>
                <th className="num">Credit</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>
                    <select
                      className="bk-cat-select"
                      aria-label={`Category for line ${String(i + 1)}`}
                      value={l.categoryId}
                      onChange={(e) => set(i, { categoryId: e.target.value })}
                    >
                      <option value="">Pick a category</option>
                      {[...new Set(shown.map((c) => c.group))].map((g) => (
                        <optgroup key={g} label={g}>
                          {shown
                            .filter((c) => c.group === g)
                            .map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.parentId ? `${nameOf.get(c.parentId) ?? ""}, ${c.name}` : c.name}
                              </option>
                            ))}
                        </optgroup>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      className="bk-je-amt"
                      aria-label={`Debit for line ${String(i + 1)}`}
                      inputMode="decimal"
                      value={l.debit}
                      onChange={(e) => set(i, { debit: e.target.value, credit: e.target.value ? "" : l.credit })}
                    />
                  </td>
                  <td>
                    <input
                      className="bk-je-amt"
                      aria-label={`Credit for line ${String(i + 1)}`}
                      inputMode="decimal"
                      value={l.credit}
                      onChange={(e) => set(i, { credit: e.target.value, debit: e.target.value ? "" : l.debit })}
                    />
                  </td>
                  <td>
                    {lines.length > 2 ? (
                      <IconButton
                        icon="close"
                        label="Take this line out"
                        onClick={() => setLines(lines.filter((_, j) => j !== i))}
                      />
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>
                  <Button
                    size="small"
                    type="tertiary"
                    icon="plus"
                    disabled={lines.length >= 20}
                    onClick={() => setLines([...lines, { categoryId: "", debit: "", credit: "" }])}
                  >
                    Add a line
                  </Button>
                </td>
                <td className="num">
                  <b>{moneyExact(debits)}</b>
                </td>
                <td className="num">
                  <b>{moneyExact(credits)}</b>
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        ) : null}
        {preset === "blank" && debits !== credits ? (
          <p className="bk-left small">Debits and credits must match. {moneyExact(Math.abs(debits - credits))} apart.</p>
        ) : null}
        <p className="muted small">
          A debit adds to a cost and takes from income. A credit adds to income and takes from a cost. Ask the accountant if
          unsure.
        </p>
      </div>
    </Modal>
  );
}
